import type { RequestPayload } from "@inertiajs/core";
import { usePage } from "@inertiajs/react";
import { Anchor, Box, Button, CloseButton, LoadingOverlay, Modal, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconFileImport } from "@tabler/icons-react";
import { calculateFileSize } from "../../helpers/fileHelper";
import { DropZone } from "../dropzone/Dropzone";

type ImportModalProps = {
  onSubmit: (values: RequestPayload) => void;
  title: string;
  opened: boolean;
  close: () => void;
  mimeTypes?: string[];
  onReject?: () => void;
  disabled?: boolean;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
  downloadPath?: string;
};

type FormErrors = {
  files: string[];
};

type FormType = {
  file?: File;
};

export function ImportModal({ title, onSubmit, opened, close, isLoading, setIsLoading, downloadPath, ...props }: ImportModalProps) {
  const { errors } = usePage<{ errors: FormErrors }>().props;

  const form = useForm<FormType>({
    mode: "controlled",
    initialValues: {
      file: undefined,
    },

    validate: {
      file: (file) => (!file ? "At least one file must be present" : null),
    },
  });

  const selectedFiles = (
    <>
      {form.values.file && (
        <Text>
          <b>{form.values.file?.name}</b> {calculateFileSize(form.values.file?.size)}
          <CloseButton size="xs" onClick={() => form.setFieldValue("file", undefined)} />
        </Text>
      )}
    </>
  );

  const onFormSubmit = (values: RequestPayload) => {
    form.reset();
    setIsLoading(true);
    onSubmit(values);
  };

  return (
    <Modal title={title} onClose={close} opened={opened}>
      <Box pos="relative">
        <LoadingOverlay visible={isLoading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
        <form onSubmit={form.onSubmit((values) => onFormSubmit(values))}>
          <Stack>
            {downloadPath && (
              <Text>
                Eine Importvorlage finden Sie{" "}
                <Anchor href={downloadPath} download>
                  hier
                </Anchor>
              </Text>
            )}
            <DropZone onDrop={(files) => form.setFieldValue("file", files[0])} error={errors?.files} multiple={false} {...props} />
            {form.values.file && (
              <div>
                <Text>Selektierte Dateien:</Text>
                {selectedFiles}
              </div>
            )}

            <Button disabled={!form.isValid()} leftSection={<IconFileImport />} type="submit">
              Importieren
            </Button>
          </Stack>
        </form>
      </Box>
    </Modal>
  );
}
